import { getMuLookupTables, writeFireColor, writeFireColorMuFast } from './firePalette';

const WARM_COLORS = [
  [15, 10, 8], [75, 18, 3], [180, 45, 2],
  [250, 115, 8], [255, 220, 90], [255, 250, 210],
];
const DEEP_FIRE_LOG_RANGE = Math.log1p(20000);
const INV_DEEP_FIRE_LOG_RANGE = 1 / DEEP_FIRE_LOG_RANGE;
export const LINEAR_RGB = Float64Array.from({ length: 256 }, (_, value) => {
  const srgb = value / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
});
const RADIUS_COLOR_ENDPOINTS = new Uint8ClampedArray(8);
const RADIUS_COLOR_ENDPOINTS32 = new Uint32Array(RADIUS_COLOR_ENDPOINTS.buffer);
const WARM_ENDPOINTS = new Uint8ClampedArray([
  WARM_COLORS[0][0], WARM_COLORS[0][1], WARM_COLORS[0][2], 255,
  WARM_COLORS[WARM_COLORS.length - 1][0],
  WARM_COLORS[WARM_COLORS.length - 1][1],
  WARM_COLORS[WARM_COLORS.length - 1][2],
  255,
]);
const WARM_ENDPOINTS32 = new Uint32Array(WARM_ENDPOINTS.buffer);
const WARM_COLOR_MIN_32 = WARM_ENDPOINTS32[0];
const WARM_COLOR_MAX_32 = WARM_ENDPOINTS32[1];
const WARM_R0 = new Float64Array([15, 75, 180, 250, 255]);
const WARM_G0 = new Float64Array([10, 18, 45, 115, 220]);
const WARM_B0 = new Float64Array([8, 3, 2, 8, 90]);
const WARM_DR = new Float64Array([60, 105, 70, 5, 0]);
const WARM_DG = new Float64Array([8, 27, 70, 105, 30]);
const WARM_DB = new Float64Array([-5, -1, 6, 82, 120]);
const ENDPOINT_PROBE_RESULT = { iterations: 0, distanceSquared: 0, escaped: true, glitch: false };
const SRGB_BUCKETS = 4096;
const COLOR_BUCKETS = 8192;
const POW_BUCKETS = 4096;
let srgbThresholds = null;
let srgbBucketLo = null;
let srgbBucketHi = null;
let muBaseTable = null;
let muSlopeTable = null;
let cachedBucketColor32 = null;
let powBaseTable = null;
let powSlopeTable = null;
let cachedPowGamma = NaN;
let cachedColorParams = null;
let cachedValueRange = null;
let cachedR0 = NaN;
let cachedR1 = NaN;
let cachedGamma = NaN;
let cachedInvSpan = 0;
let cachedScale8192 = 0;
let cachedUError = 0;
let cachedMaxPowErr = 0;
let cachedScale5 = 0;
let cachedDLo = 0;
let cachedDHi = 0;
let cachedFastRange = false;

function buildFastRangeTables(r0, r1, gamma) {
  if (!muBaseTable) {
    ({ muBaseTable, muSlopeTable } = getMuLookupTables());
  }
  if (!cachedBucketColor32) {
    cachedBucketColor32 = new Uint32Array(COLOR_BUCKETS);
  }
  if (gamma !== 1 && gamma !== cachedPowGamma) {
    if (!powBaseTable) {
      powBaseTable = new Float64Array(POW_BUCKETS);
      powSlopeTable = new Float64Array(POW_BUCKETS);
    }
    let prevP = 0;
    let maxErr = 0;
    for (let b = 0; b < POW_BUCKETS; b++) {
      const u0 = b / POW_BUCKETS;
      const u1 = (b + 1) / POW_BUCKETS;
      const nextP = 5 * (u1 ** gamma);
      powBaseTable[b] = prevP;
      powSlopeTable[b] = nextP - prevP;
      const uMid = 0.5 * (u0 + u1);
      const pMidExact = 5 * (uMid ** gamma);
      const pMidApprox = prevP + 0.5 * (nextP - prevP);
      const err = Math.abs(pMidExact - pMidApprox);
      if (err > maxErr) maxErr = err;
      prevP = nextP;
    }
    cachedMaxPowErr = maxErr * 1.1 + 1e-12;
    cachedPowGamma = gamma;
  }
  const invSpan = 1 / (r1 - r0);
  const uErr = 3.6e-6 * invSpan + 1e-12;
  cachedInvSpan = invSpan;
  cachedScale8192 = COLOR_BUCKETS * invSpan;
  cachedUError = 3.6e-6 * invSpan;
  const evalAtU = (u, off) => {
    const exp = gamma === 1 ? u : u ** gamma;
    const pos = exp * 5;
    const idx = pos >= 4 ? 4 : pos | 0;
    const blend = pos - idx;
    const a = WARM_COLORS[idx];
    const b = WARM_COLORS[idx + 1];
    RADIUS_COLOR_ENDPOINTS[off] = a[0] + (b[0] - a[0]) * blend;
    RADIUS_COLOR_ENDPOINTS[off + 1] = a[1] + (b[1] - a[1]) * blend;
    RADIUS_COLOR_ENDPOINTS[off + 2] = a[2] + (b[2] - a[2]) * blend;
    RADIUS_COLOR_ENDPOINTS[off + 3] = 255;
    return idx;
  };
  for (let k = 0; k < COLOR_BUCKETS; k++) {
    const uMin = k / COLOR_BUCKETS - uErr;
    const uMax = (k + 1) / COLOR_BUCKETS + uErr;
    if (uMin <= 0 || uMax >= 1) {
      cachedBucketColor32[k] = 0;
      continue;
    }
    const idx0 = evalAtU(uMin, 0);
    const idx1 = evalAtU(uMax, 4);
    cachedBucketColor32[k] = (idx0 === idx1 && RADIUS_COLOR_ENDPOINTS32[0] === RADIUS_COLOR_ENDPOINTS32[1])
      ? RADIUS_COLOR_ENDPOINTS32[0] : 0;
  }
}

function syncColorRangeCache(colorParams) {
  const range = colorParams?.valueRange;
  const rawGamma = colorParams?.gamma;
  const resolvedGamma = rawGamma === undefined ? 1 : rawGamma;
  if (colorParams === cachedColorParams && range === cachedValueRange) {
    if (!cachedFastRange) return false;
    if (range[0] === cachedR0 && range[1] === cachedR1 && resolvedGamma === cachedGamma) {
      return true;
    }
  }
  cachedColorParams = colorParams;
  cachedValueRange = range;
  if (validValueRange(range)
    && colorParams.useMu !== false
    && resolvedGamma > 0 && Number.isFinite(resolvedGamma)) {
    const r0 = range[0];
    const r1 = range[1];
    const scale5 = 5 / (r1 - r0);
    const tol = 125 * scale5 * 3.6e-6 + 1e-9;
    if (tol < 0.25) {
      if (!cachedFastRange || r0 !== cachedR0 || r1 !== cachedR1 || resolvedGamma !== cachedGamma) {
        cachedR0 = r0;
        cachedR1 = r1;
        cachedGamma = resolvedGamma;
        cachedScale5 = scale5;
        cachedDLo = 0.5 - tol;
        cachedDHi = 0.5 + tol;
        buildFastRangeTables(r0, r1, resolvedGamma);
      }
      cachedFastRange = true;
      return true;
    }
  }
  cachedFastRange = false;
  return false;
}

function initSrgbLookup() {
  const u8 = new Uint8ClampedArray(1);
  const byteAt = v => {
    u8[0] = 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);
    return u8[0];
  };
  srgbThresholds = new Float64Array(256);
  for (let k = 0; k < 255; k++) {
    const s = (k + 0.5) / 255;
    const approx = s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    let lo = Math.max(0, approx - 1e-7);
    let hi = Math.min(1, approx + 1e-7);
    for (let it = 0; it < 35; it++) {
      const mid = 0.5 * (lo + hi);
      if (byteAt(mid) <= k) lo = mid;
      else hi = mid;
    }
    srgbThresholds[k] = hi;
  }
  srgbThresholds[255] = Infinity;
  srgbBucketLo = new Uint8Array(SRGB_BUCKETS);
  srgbBucketHi = new Uint8Array(SRGB_BUCKETS);
  let cur = 0;
  for (let b = 0; b < SRGB_BUCKETS; b++) {
    const v0 = b / SRGB_BUCKETS;
    const v1 = (b + 1) / SRGB_BUCKETS;
    while (cur < 255 && v0 >= srgbThresholds[cur]) cur++;
    srgbBucketLo[b] = cur;
    let end = cur;
    while (end < 255 && v1 >= srgbThresholds[end]) end++;
    srgbBucketHi[b] = end;
  }
}

export function fromLinear(value) {
  if (!srgbThresholds) initSrgbLookup();
  if (!(value > 0)) return 0;
  if (value >= 1) return 255;
  const b = (value * SRGB_BUCKETS) | 0;
  let lo = srgbBucketLo[b];
  const hi = srgbBucketHi[b];
  while (lo < hi && value >= srgbThresholds[lo]) lo++;
  return lo;
}

export function validValueRange(range) {
  return Array.isArray(range) && range.length === 2
    && range[0] < range[1] && Number.isFinite(range[0]) && Number.isFinite(range[1]);
}

export function sameValueRange(a, b) {
  if (a === b) return true;
  if (!validValueRange(a) || !validValueRange(b)) return false;
  return a[0] === b[0] && a[1] === b[1];
}

function writeRgb(pixels, offset, r, g, b) {
  pixels[offset] = r;
  pixels[offset + 1] = g;
  pixels[offset + 2] = b;
  pixels[offset + 3] = 255;
}

/** Smooth escape values feed either the original fire colors or a wider deep-view ramp. */
export function writePixel(pixels, offset, result, colorParams = {}) {
  if (!result.escaped) {
    writeRgb(pixels, offset, 10, 9, 14);
    return;
  }
  const certifiedPacked = result._certifiedColor32;
  if (certifiedPacked !== undefined) {
    writeRgb(
      pixels,
      offset,
      certifiedPacked & 0xff,
      (certifiedPacked >>> 8) & 0xff,
      (certifiedPacked >>> 16) & 0xff,
    );
    return;
  }
  if (syncColorRangeCache(colorParams)) {
    const it = result.iterations;
    const d2 = result.distanceSquared;
    if (d2 >= 4 && d2 < 260) {
      if (it + 1.529 <= cachedR0) {
        writeRgb(pixels, offset, 15, 10, 8);
        return;
      }
      if (it - 1.472 >= cachedR1) {
        writeRgb(pixels, offset, 255, 250, 210);
        return;
      }
      const u = (d2 - 4) * 64;
      const b = u | 0;
      const vApprox = it + muBaseTable[b] + muSlopeTable[b] * (u - b);
      if (vApprox + 3.6e-6 <= cachedR0) {
        writeRgb(pixels, offset, 15, 10, 8);
        return;
      }
      if (vApprox - 3.6e-6 >= cachedR1) {
        writeRgb(pixels, offset, 255, 250, 210);
        return;
      }
      const k = ((vApprox - cachedR0) * cachedScale8192) | 0;
      const c32 = (k >>> 0) < COLOR_BUCKETS ? cachedBucketColor32[k] : 0;
      if (c32 !== 0) {
        writeRgb(pixels, offset, c32 & 0xff, (c32 >>> 8) & 0xff, (c32 >>> 16) & 0xff);
        return;
      }
      if (cachedGamma === 1) {
        const pLow = (vApprox - 3.6e-6 - cachedR0) * cachedScale5;
        const pHigh = (vApprox + 3.6e-6 - cachedR0) * cachedScale5;
        const idx = pLow >= 4 ? 4 : pLow | 0;
        if (idx === (pHigh >= 4 ? 4 : pHigh | 0) && pLow > 0 && pHigh < 5) {
          const blend = (vApprox - cachedR0) * cachedScale5 - idx;
          const c0 = WARM_R0[idx] + WARM_DR[idx] * blend;
          const rByte = (c0 + cachedDLo) | 0;
          if (rByte === ((c0 + cachedDHi) | 0)) {
            const c1 = WARM_G0[idx] + WARM_DG[idx] * blend;
            const gByte = (c1 + cachedDLo) | 0;
            if (gByte === ((c1 + cachedDHi) | 0)) {
              const c2 = WARM_B0[idx] + WARM_DB[idx] * blend;
              const bByte = (c2 + cachedDLo) | 0;
              if (bByte === ((c2 + cachedDHi) | 0)) {
                writeRgb(pixels, offset, rByte, gByte, bByte);
                return;
              }
            }
          }
        }
      } else {
        const uExp = (vApprox - cachedR0) * cachedInvSpan;
        if (uExp > cachedUError && uExp + cachedUError < 1) {
          const uScaled = uExp * POW_BUCKETS;
          const bPow = uScaled | 0;
          const slope = powSlopeTable[bPow];
          const pApprox = powBaseTable[bPow] + slope * (uScaled - bPow);
          const pErr = cachedUError * POW_BUCKETS * slope + cachedMaxPowErr;
          const pLow = pApprox - pErr;
          const pHigh = pApprox + pErr;
          const idx = pLow >= 4 ? 4 : pLow | 0;
          if (idx === (pHigh >= 4 ? 4 : pHigh | 0) && pLow > 0 && pHigh < 5) {
            const blend = pApprox - idx;
            const tol = 125 * pErr + 1e-9;
            if (tol < 0.25) {
              const dLo = 0.5 - tol;
              const dHi = 0.5 + tol;
              const c0 = WARM_R0[idx] + WARM_DR[idx] * blend;
              const rByte = (c0 + dLo) | 0;
              if (rByte === ((c0 + dHi) | 0)) {
                const c1 = WARM_G0[idx] + WARM_DG[idx] * blend;
                const gByte = (c1 + dLo) | 0;
                if (gByte === ((c1 + dHi) | 0)) {
                  const c2 = WARM_B0[idx] + WARM_DB[idx] * blend;
                  const bByte = (c2 + dLo) | 0;
                  if (bByte === ((c2 + dHi) | 0)) {
                    writeRgb(pixels, offset, rByte, gByte, bByte);
                    return;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  let value = result.iterations;
  const d2 = result.distanceSquared;
  const range = colorParams.valueRange;
  const hasRange = validValueRange(range);
  if (hasRange) {
    const useMu = colorParams.useMu !== false && d2 > 1 && d2 < Infinity;
    if (useMu && d2 >= 4) {
      if (value + 1.529 <= range[0]) {
        writeRgb(pixels, offset, 15, 10, 8);
        return;
      }
      if (d2 <= 65536 && value - 1.472 >= range[1]) {
        writeRgb(pixels, offset, 255, 250, 210);
        return;
      }
    }
    if (useMu) {
      value += 1 - Math.log2(0.5 * Math.log(d2));
    }
    if (value <= range[0]) {
      writeRgb(pixels, offset, 15, 10, 8);
      return;
    }
    if (value >= range[1]) {
      writeRgb(pixels, offset, 255, 250, 210);
      return;
    }
  } else if (colorParams.palette !== 'fire') {
    if (colorParams.useMu !== false) {
      writeFireColorMuFast(pixels, offset, value, d2);
    } else {
      writeFireColor(pixels, offset, value);
    }
    return;
  } else if (colorParams.useMu !== false && d2 > 1 && d2 < Infinity) {
    value += 1 - Math.log2(0.5 * Math.log(d2));
  }
  let exposure = hasRange
    ? (value - range[0]) / (range[1] - range[0])
    : Math.min(1, Math.log1p(Math.max(0, value)) * INV_DEEP_FIRE_LOG_RANGE);
  const gamma = colorParams.gamma;
  if (gamma !== undefined && gamma !== 1 && gamma > 0 && Number.isFinite(gamma)
    && exposure > 0 && exposure < 1) {
    exposure = exposure ** gamma;
  }
  const position = exposure * (WARM_COLORS.length - 1);
  const index = position >= WARM_COLORS.length - 2 ? WARM_COLORS.length - 2 : position | 0;
  const blend = position - index;
  const a = WARM_COLORS[index];
  const b = WARM_COLORS[index + 1];
  writeRgb(
    pixels,
    offset,
    a[0] + (b[0] - a[0]) * blend,
    a[1] + (b[1] - a[1]) * blend,
    a[2] + (b[2] - a[2]) * blend,
  );
}

function ambiguousColor(result, mutate) {
  if (mutate) {
    result.escaped = false;
    result.glitch = true;
    result.glitchReason = 'color-ambiguous';
    return result;
  }
  return { ...result, escaped: false, glitch: true,
    glitchReason: 'color-ambiguous' };
}

function certifyStableColor(result, norm, color32, mutate) {
  if (mutate) {
    result.distanceSquared = norm;
    result.stableContinuedGrowth = true;
    result._certifiedColor32 = color32;
    return result;
  }
  return { ...result, distanceSquared: norm, stableContinuedGrowth: true };
}

/** Reject a continued escape when its full radius interval could change a byte. */
export function stablePerturbedColor(result, colorParams = {}, mutate = false) {
  if (!result || !result.continuedGrowth || result.glitch) return result;
  if (!result.escaped || colorParams.useMu === false) {
    if (mutate) {
      result.stableContinuedGrowth = true;
      return result;
    }
    return { ...result, stableContinuedGrowth: true };
  }
  const norm = result.radiusNormSquared;
  const error = result.radiusErrorBound;
  const lower = result.radiusLowerSquared ?? norm - error;
  const upper = result.radiusUpperSquared ?? norm + error;
  if (!Number.isFinite(lower) || !Number.isFinite(upper) || lower < 4) {
    return ambiguousColor(result, mutate);
  }
  if (upper < 260 && syncColorRangeCache(colorParams)) {
    const it = result.iterations;
    if (it + 1.529 <= cachedR0) {
      return certifyStableColor(result, norm, WARM_COLOR_MIN_32, mutate);
    }
    if (it - 1.472 >= cachedR1) {
      return certifyStableColor(result, norm, WARM_COLOR_MAX_32, mutate);
    }
    const uMuL = (lower - 4) * 64;
    const bMuL = uMuL | 0;
    const vLowApprox = it + muBaseTable[bMuL] + muSlopeTable[bMuL] * (uMuL - bMuL);
    if (vLowApprox + 3.6e-6 <= cachedR0) {
      return certifyStableColor(result, norm, WARM_COLOR_MIN_32, mutate);
    }
    const uMuH = (upper - 4) * 64;
    const bMuH = uMuH | 0;
    const vHighApprox = it + muBaseTable[bMuH] + muSlopeTable[bMuH] * (uMuH - bMuH);
    if (vHighApprox - 3.6e-6 >= cachedR1) {
      return certifyStableColor(result, norm, WARM_COLOR_MAX_32, mutate);
    }
    const kMin = ((vHighApprox - cachedR0) * cachedScale8192) | 0;
    const kMax = ((vLowApprox - cachedR0) * cachedScale8192) | 0;
    if (kMin === kMax && (kMin >>> 0) < COLOR_BUCKETS) {
      const c32 = cachedBucketColor32[kMin];
      if (c32 !== 0) {
        return certifyStableColor(result, norm, c32, mutate);
      }
    }
  }
  const range = colorParams.valueRange;
  const hasRange = validValueRange(range);
  if (hasRange || colorParams.palette === 'fire') {
    if (hasRange) {
      if (result.iterations + 1.529 <= range[0]) {
        return certifyStableColor(result, norm, WARM_COLOR_MIN_32, mutate);
      }
      if (upper <= 65536 && result.iterations - 1.472 >= range[1]) {
        return certifyStableColor(result, norm, WARM_COLOR_MAX_32, mutate);
      }
    }
    const maxSeg = WARM_COLORS.length - 2;
    const scale = WARM_COLORS.length - 1;
    const logLower = Math.log(lower);
    const vLow = result.iterations + 1 - Math.log2(0.5 * logLower);
    const gamma = colorParams.gamma;
    const hasGamma = gamma !== undefined && gamma !== 1 && gamma > 0 && Number.isFinite(gamma);
    if (hasRange && upper - lower <= 1e-3 * lower) {
      const r0 = range[0];
      if (vLow <= r0) {
        return certifyStableColor(result, norm, WARM_COLOR_MIN_32, mutate);
      }
      const maxVDelta = (upper - lower) / (0.6931471805599453 * lower * logLower);
      if (vLow - maxVDelta >= range[1]) {
        return certifyStableColor(result, norm, WARM_COLOR_MAX_32, mutate);
      }
      const invSpan = 1 / (range[1] - r0);
      let eLow = Math.max(0, Math.min(1, (vLow - r0) * invSpan));
      const maxEDelta = maxVDelta * invSpan;
      if (eLow > maxEDelta && eLow < 1) {
        let maxPDelta;
        if (hasGamma) {
          const eMin = eLow - maxEDelta;
          const powLow = eLow ** gamma;
          const slope = gamma <= 1 ? gamma * (eMin ** (gamma - 1)) : gamma;
          maxPDelta = maxEDelta * slope * scale * 1.0001;
          eLow = powLow;
        } else {
          maxPDelta = maxEDelta * scale * 1.0001;
        }
        const pLow = eLow * scale;
        const segLow = pLow >= maxSeg ? maxSeg : pLow | 0;
        const fLow = pLow - segLow;
        if (fLow > maxPDelta) {
          const a = WARM_COLORS[segLow];
          const b = WARM_COLORS[segLow + 1];
          const c0 = a[0] + (b[0] - a[0]) * fLow;
          const c1 = a[1] + (b[1] - a[1]) * fLow;
          const c2 = a[2] + (b[2] - a[2]) * fLow;
          const tol = 140 * maxPDelta + 1e-7;
          if (Math.abs((c0 - Math.floor(c0)) - 0.5) > tol
            && Math.abs((c1 - Math.floor(c1)) - 0.5) > tol
            && Math.abs((c2 - Math.floor(c2)) - 0.5) > tol) {
            writeRgb(RADIUS_COLOR_ENDPOINTS, 0, c0, c1, c2);
            return certifyStableColor(result, norm, RADIUS_COLOR_ENDPOINTS32[0], mutate);
          }
        }
      }
    }
    const vHigh = result.iterations + 1 - Math.log2(0.5 * Math.log(upper));
    let eLow;
    let eHigh;
    if (hasRange) {
      const r0 = range[0];
      if (vLow <= r0 && vHigh <= r0) {
        return certifyStableColor(result, norm, WARM_COLOR_MIN_32, mutate);
      }
      if (vLow >= range[1] && vHigh >= range[1]) {
        return certifyStableColor(result, norm, WARM_COLOR_MAX_32, mutate);
      }
      const invSpan = 1 / (range[1] - r0);
      eLow = Math.max(0, Math.min(1, (vLow - r0) * invSpan));
      eHigh = Math.max(0, Math.min(1, (vHigh - r0) * invSpan));
    } else {
      eLow = Math.min(1, Math.log1p(Math.max(0, vLow)) * INV_DEEP_FIRE_LOG_RANGE);
      eHigh = Math.min(1, Math.log1p(Math.max(0, vHigh)) * INV_DEEP_FIRE_LOG_RANGE);
    }
    if (hasGamma) {
      if (eLow > 0 && eLow < 1) eLow = eLow ** gamma;
      if (eHigh > 0 && eHigh < 1) eHigh = eHigh ** gamma;
    }
    const pLow = eLow * scale;
    const pHigh = eHigh * scale;
    const segLow = pLow >= maxSeg ? maxSeg : pLow | 0;
    const segHigh = pHigh >= maxSeg ? maxSeg : pHigh | 0;
    if (segLow !== segHigh) return ambiguousColor(result, mutate);
    const a = WARM_COLORS[segLow];
    const b = WARM_COLORS[segLow + 1];
    const fLow = pLow - segLow;
    const fHigh = pHigh - segLow;
    writeRgb(
      RADIUS_COLOR_ENDPOINTS,
      0,
      a[0] + (b[0] - a[0]) * fLow,
      a[1] + (b[1] - a[1]) * fLow,
      a[2] + (b[2] - a[2]) * fLow,
    );
    writeRgb(
      RADIUS_COLOR_ENDPOINTS,
      4,
      a[0] + (b[0] - a[0]) * fHigh,
      a[1] + (b[1] - a[1]) * fHigh,
      a[2] + (b[2] - a[2]) * fHigh,
    );
    if (RADIUS_COLOR_ENDPOINTS32[0] !== RADIUS_COLOR_ENDPOINTS32[1]) {
      return ambiguousColor(result, mutate);
    }
    return certifyStableColor(result, norm, RADIUS_COLOR_ENDPOINTS32[0], mutate);
  }
  ENDPOINT_PROBE_RESULT.iterations = result.iterations;
  ENDPOINT_PROBE_RESULT.distanceSquared = lower;
  writePixel(RADIUS_COLOR_ENDPOINTS, 0, ENDPOINT_PROBE_RESULT, colorParams);
  ENDPOINT_PROBE_RESULT.distanceSquared = upper;
  writePixel(RADIUS_COLOR_ENDPOINTS, 4, ENDPOINT_PROBE_RESULT, colorParams);
  if (RADIUS_COLOR_ENDPOINTS32[0] !== RADIUS_COLOR_ENDPOINTS32[1]) {
    return ambiguousColor(result, mutate);
  }
  return certifyStableColor(result, norm, RADIUS_COLOR_ENDPOINTS32[0], mutate);
}
